"use client";

import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import CheckoutReviewOrderDetails from "@/app/checkout/(squeeze)/review/checkoutReviewOrderDetails";
import VendorSection from "@/app/checkout/(squeeze)/review/vendorSection";
import CheckoutNav from "@/app/checkout/checkoutNav";
import OrderSummary from "@/app/checkout/orderSummary";
import Preloader from "@/app/components/preloader";
import useAnalyticsManager from "@/app/hooks/useAnalyticsManager";
import useCheckout from "@/app/hooks/useCheckout";
import useStickySummaryShadow from "@/app/hooks/useStickySummaryShadow";
import Button from "@/components/design-system/button";
import getCheckoutResponse, { CheckoutResponse } from "@/fetch/getCheckoutResponse";
import { usePlaceOrderMutation } from "@/store/checkoutApi";
import {
  CheckoutCart,
  setCheckoutReviewDetails,
  setOrderStatusError,
  setPlaceOrderConfirmationDetails,
  setShoppingCartCopy,
} from "@/store/checkoutSlice";
import { useGetUserQuery } from "@/store/userApi";

import styles from "./checkoutReviewPage.module.scss";

export const formatter = new Intl.NumberFormat("default", {
  style: "currency",
  currency: "USD",
});

export enum CheckoutReviewErrorCodes {
  CARD_INVALID = "CARD_INVALID",
  CARD_EXPIRED = "CARD_EXPIRED",
  ADDRESS_INVALID = "ADDRESS_INVALID",
  CART_CHANGED = "CART_CHANGED",
  SHIPPING_COST_CHANGED = "SHIPPING_COST_CHANGED",
  LICENSE_REQUIRED = "LICENSE_REQUIRED",
  UNATHORIZED = "UNATHORIZED",
  PRICE_BREAK_INVALID = "PRICE_BREAK_INVALID",
  INVENTORY_UNAVAILABLE = "INVENTORY_UNAVAILABLE",
}
export default function CheckoutReviewPage({
  isConfirmationPage,
  placeOrderConfirmationDetails,
}: {
  isConfirmationPage: boolean;
  placeOrderConfirmationDetails?: CheckoutResponse;
}) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { data: user } = useGetUserQuery();
  const [placeOrder] = usePlaceOrderMutation();
  const { dispatchCheckoutStep, dispatchPageView, dispatchPlaceOrder, dispatchPurchase } = useAnalyticsManager();
  const {
    checkoutCart,
    setupComplete,
    chosenCreditCard,
    chosenShippingAddress,
    addresses,
    account,
    checkoutIsLoading,
    nextStep,
    goToNextStep,
  } = useCheckout({
    performInitialSetup: true,
  });

  const loggedInUser = user as LoggedInUserState;
  const [stepSubmitted, setStepSubmitted] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(true);
  const { stickySentinelRef, summaryIsStuck } = useStickySummaryShadow([checkoutLoading]);
  const [placeOrderLoading, setPlaceOrderLoading] = useState(false);
  const [checkoutResponse, setCheckoutResponse] = useState<CheckoutResponse | null>();
  const cartBouncedRef = useRef(false);
  const pathname = usePathname();

  useEffect(() => {
    if (isConfirmationPage && placeOrderConfirmationDetails) {
      setCheckoutResponse(placeOrderConfirmationDetails as CheckoutResponse);
      setCheckoutLoading(false);
    }
  }, [isConfirmationPage, placeOrderConfirmationDetails]);

  useEffect(() => {
    if (checkoutCart !== null && user !== undefined && !isConfirmationPage && stepSubmitted === false) {
      dispatchCheckoutStep({ step: 6, shoppingCartState: checkoutCart });
      dispatchPageView({
        userState: user,
      });
      setStepSubmitted(true);
    }
  }, [checkoutCart, isConfirmationPage, dispatchCheckoutStep, stepSubmitted, dispatchPageView, user]);

  const stepRedirectedRef = useRef(false);
  useEffect(() => {
    if (
      !isConfirmationPage &&
      !checkoutIsLoading &&
      setupComplete &&
      account &&
      addresses &&
      nextStep !== "review" &&
      !stepRedirectedRef.current
    ) {
      stepRedirectedRef.current = true;
      goToNextStep({ replace: true });
    }
  }, [account, addresses, checkoutIsLoading, goToNextStep, isConfirmationPage, nextStep, setupComplete]);

  // Codes that send the shopper back to the cart mean the cart just posted no longer
  // matches the server's, so checkout's copy of it is dropped: useCheckout seeds that copy
  // only while it is null, and the cart page repairs the cart after the redirect (replacing
  // vendors, correcting quantities), so a kept copy makes the next attempt post stale
  // products and fail the same validation. The latch stops this page re-posting while the
  // redirect is in flight, since clearing the copy makes useCheckout seed a fresh one.
  const bounceToCart = useCallback(
    (target: string) => {
      cartBouncedRef.current = true;
      dispatch(setShoppingCartCopy(null));
      router.push(target);
    },
    [dispatch, router],
  );

  const handleStringResponse = useCallback(
    (response: string) => {
      switch (response) {
        case CheckoutReviewErrorCodes.CARD_INVALID:
        case CheckoutReviewErrorCodes.CARD_EXPIRED:
          router.replace("/checkout/billing");
          break;
        case CheckoutReviewErrorCodes.ADDRESS_INVALID:
          router.replace("/checkout/shipping");
          break;
        case CheckoutReviewErrorCodes.CART_CHANGED:
        case CheckoutReviewErrorCodes.PRICE_BREAK_INVALID:
        case CheckoutReviewErrorCodes.INVENTORY_UNAVAILABLE:
          bounceToCart("/shopping-cart?error=cart-has-changed");
          break;
        case CheckoutReviewErrorCodes.SHIPPING_COST_CHANGED:
          bounceToCart("/shopping-cart?error=shipping-cost-has-changed");
          break;
        case CheckoutReviewErrorCodes.LICENSE_REQUIRED:
          router.replace("/checkout/license");
          break;
        case CheckoutReviewErrorCodes.UNATHORIZED:
          router.replace("/login?origin=" + pathname);
          break;
        default:
          bounceToCart("/shopping-cart");
          break;
      }
    },
    [bounceToCart, pathname, router],
  );

  useEffect(() => {
    async function fetchCheckoutResponse() {
      const checkoutRequest = {
        ...checkoutCart,
        vendorOrders: checkoutCart?.vendorOrders.map((vendorOrder) => ({
          ...vendorOrder,
          products: vendorOrder.products.map((product) => ({
            ...product,
            smallImagePath: product.mediaPath,
          })),
        })),
        firstName: loggedInUser?.FirstName,
        lastName: loggedInUser?.LastName,
        emailAddress: loggedInUser?.EmailAddress,
        addressId: chosenShippingAddress,
        cardId: chosenCreditCard,
      } as CheckoutRequest;
      try {
        const response = await getCheckoutResponse(checkoutRequest);
        if (typeof response === "string") {
          handleStringResponse(response);
        } else {
          dispatch(setCheckoutReviewDetails(response as CheckoutResponse));
          setCheckoutResponse(response as CheckoutResponse);
          setCheckoutLoading(false);
        }
      } catch (err) {
        router.push("/shopping-cart");
      }
    }
    if (
      !cartBouncedRef.current &&
      checkoutCart &&
      checkoutCart.vendorOrders &&
      !isConfirmationPage &&
      chosenCreditCard &&
      chosenShippingAddress
    )
      fetchCheckoutResponse();
  }, [
    checkoutCart,
    loggedInUser?.EmailAddress,
    loggedInUser?.FirstName,
    loggedInUser?.LastName,
    router,
    isConfirmationPage,
    dispatch,
    handleStringResponse,
    chosenCreditCard,
    chosenShippingAddress,
  ]);

  const onPlaceOrder = useCallback(async (): Promise<void> => {
    console.log("Placing order...");
    setPlaceOrderLoading(true);
    if (checkoutResponse !== undefined && checkoutResponse !== null) {
      const intwoDayVariation = typeof window !== "undefined" ? localStorage.getItem("inTwoDayDeliveryTest") : null;

      const isTwoDayVariationAllowed = intwoDayVariation === "true";

      const modifiedCheckoutResponse = {
        ...checkoutResponse,
        vendorOrders: checkoutResponse.vendorOrders.map((vendorOrder) => ({
          ...vendorOrder,
          products: vendorOrder.products.map((product) => ({
            ...product,
            twoDayDeliverySw:
              !isTwoDayVariationAllowed && product.twoDayDeliverySw === true ? false : product.twoDayDeliverySw,
          })),
        })),
      };
      placeOrder(modifiedCheckoutResponse)
        .unwrap()
        .then((response) => {
          if (typeof response === "string") {
            handleStringResponse(response); // Handle string response errors
          } else {
            // Success scenario
            if (response) {
              if (response.statusCode === "SC_CARD_DECLINED_ERROR") {
                dispatch(setOrderStatusError(response.statusMessage));
                router.push("/checkout/billing");
              } else {
                const orderNumber = response.coId;
                dispatch(setCheckoutReviewDetails(null));
                dispatch(setPlaceOrderConfirmationDetails(response));
                const selectedShippingAddress = addresses?.find((address) => address.Id === chosenShippingAddress);
                dispatchPurchase(response);
                dispatchPlaceOrder({ placeOrderConfirmationDetails: response, selectedShippingAddress });
                router.replace("/checkout/confirmation/" + orderNumber); // Redirect to confirmation page
              }
            } else {
              router.push("/shopping-cart"); // Redirect to shopping-cart page
            }
          }
        })
        .catch((error) => {
          console.error("Error placing order:", error);
          // From the transformErrorResponse in placeOrder,
          // We are converting to a single string message to idenntify and redirect
          if (error) {
            handleStringResponse(error as string);
          } else {
            router.push("/shopping-cart");
          }
        });
    }
  }, [
    addresses,
    checkoutResponse,
    chosenShippingAddress,
    dispatch,
    dispatchPlaceOrder,
    dispatchPurchase,
    handleStringResponse,
    placeOrder,
    router,
  ]);

  if (checkoutLoading) {
    return (
      <>
        <main>
          <CheckoutNav currentPage="review" />
          <Preloader />
        </main>
        <aside>
          <OrderSummary nextStepButton={<></>} />
        </aside>
      </>
    );
  }

  return (
    <>
      <main>
        {!isConfirmationPage && <CheckoutNav currentPage="review" />}
        <section className={styles.checkoutSubsection}>
          {!isConfirmationPage && <h1>Review and Place Order</h1>}
          <CheckoutReviewOrderDetails isConfirmationPage={isConfirmationPage} />
          <VendorSection
            checkoutResponse={checkoutResponse as CheckoutResponse}
            isConfirmationPage={isConfirmationPage}
          />
        </section>
      </main>
      <aside>
        <OrderSummary
          stuck={summaryIsStuck}
          checkoutResponse={checkoutResponse as CheckoutResponse}
          showNextStepButtonOnDesktop={!isConfirmationPage}
          isConfirmationPage={isConfirmationPage}
          nextStepButton={
            !isConfirmationPage && (
              <Button
                variant="primary"
                color="teal"
                size="large"
                text="Place Your Order"
                showLeftIcon={false}
                showRightIcon
                loading={placeOrderLoading}
                onClick={onPlaceOrder}
              />
            )
          }
        />
      </aside>
      <div ref={stickySentinelRef} className={styles.stickySentinel} aria-hidden />
    </>
  );
}

export type CheckoutRequest = CheckoutCart & {
  firstName: string;
  lastName: string;
  emailAddress: string;
  addressId: number;
  cardId: string;
};
